import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useClients } from '../../../context/ClientContext';
import { Badge, Button, Card, Input } from '../../ui';

interface TagsSectionProps {
  clientId: string;
  tags: string[];
}

export default function TagsSection({ clientId, tags }: TagsSectionProps) {
  const { updateClientTags } = useClients();
  const [localTags, setLocalTags] = useState<string[]>(tags);
  const [newTag, setNewTag] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const addTag = async () => {
    if (newTag.trim() && !localTags.includes(newTag.trim())) {
      const updatedTags = [...localTags, newTag.trim()];
      setLocalTags(updatedTags);
      setNewTag('');
      setIsLoading(true);
      await updateClientTags(clientId, updatedTags);
      setIsLoading(false);
    }
  };

  const removeTag = async (tagToRemove: string) => {
    const updatedTags = localTags.filter((tag) => tag !== tagToRemove);
    setLocalTags(updatedTags);
    setIsLoading(true);
    await updateClientTags(clientId, updatedTags);
    setIsLoading(false);
  };

  return (
    <Card eyebrow="Tags" title={localTags.length > 0 ? `${localTags.length} applied` : 'No tags'}>
      {localTags.length > 0 ? (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {localTags.map((tag) => (
            <span key={tag} style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
              <Badge tone="neutral">{tag}</Badge>
              <button
                className="pressable"
                aria-label={`Remove ${tag}`}
                title={`Remove ${tag}`}
                onClick={() => removeTag(tag)}
                disabled={isLoading}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text-faint)',
                  cursor: isLoading ? 'wait' : 'pointer',
                  padding: '2px 4px',
                  display: 'inline-flex',
                  opacity: isLoading ? 0.5 : 1,
                }}
              >
                <FontAwesomeIcon icon={faXmark} style={{ fontSize: '11px' }} />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p style={{ margin: '0 0 16px', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
          No tags yet. Add one to organize this client.
        </p>
      )}

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', maxWidth: '360px' }}>
        <Input
          fieldSize="sm"
          placeholder="Add a tag..."
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTag();
            }
          }}
          disabled={isLoading}
        />
        <Button size="sm" variant="secondary" onClick={addTag} disabled={isLoading || !newTag.trim()}>
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: '11px', marginRight: '6px' }} />
          Add Tag
        </Button>
      </div>
    </Card>
  );
}
